import { useState, useMemo } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { type components as AdminComponents } from "@njbsoft/types/admin";
import { useAdminOrgs, type AdminOrgsParams } from "@/hooks/use-admin-orgs";
import { useDebounce } from "@/hooks/use-debounce";
import { DataTable } from "@/components/data-table";
import { Pagination } from "@/components/pagination";
import { OrgStatusBadge } from "@/components/org-status-badge";
import { CreateOrgModal } from "@/components/create-org-modal";
import { formatDate } from "@njbsoft/utils";

type AdminOrg = AdminComponents["schemas"]["AdminOrg"];

export const Route = createFileRoute("/_admin/orgs/")({
  component: OrgsListPage,
});

const PAGE_SIZE = 20;

const columnHelper = createColumnHelper<AdminOrg>();

function OrgsListPage() {
  const navigate = useNavigate();

  // Filter state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  // Build query params
  const params: AdminOrgsParams = useMemo(() => {
    const p: AdminOrgsParams = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (debouncedSearch) p.search = debouncedSearch;
    if (statusFilter !== "all") p.status = statusFilter as AdminOrgsParams["status"];
    if (sorting.length > 0 && sorting[0]) {
      p.sort = sorting[0].id as AdminOrgsParams["sort"];
      p.order = sorting[0].desc ? "desc" : "asc";
    }
    return p;
  }, [debouncedSearch, statusFilter, page, sorting]);

  const { data, isLoading } = useAdminOrgs(params);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Name",
        enableSorting: true,
      }),
      columnHelper.accessor("slug", {
        header: "Slug",
        enableSorting: true,
        cell: (info) => <span className="font-mono text-xs text-slate-500">{info.getValue()}</span>,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        enableSorting: false,
        cell: (info) => <OrgStatusBadge status={info.getValue()} />,
      }),
      columnHelper.accessor("member_count", {
        header: "Members",
        enableSorting: false,
      }),
      columnHelper.accessor("created_at", {
        header: "Created",
        enableSorting: true,
        cell: (info) => formatDate(info.getValue()),
      }),
    ],
    [],
  );

  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  function handleRowClick(org: AdminOrg) {
    void navigate({ to: "/orgs/$orgId", params: { orgId: org.id } });
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    setPage(1);
  }

  function handleStatusChange(value: string) {
    setStatusFilter(value);
    setPage(1);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Organizations</h1>
        <button
          type="button"
          onClick={() => setCreateModalOpen(true)}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
        >
          Create Organization
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search by name or slug..."
          value={search}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />

        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value)}
          className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        >
          <option value="all">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.orgs ?? []}
        sorting={sorting}
        onSortingChange={setSorting}
        isLoading={isLoading}
        emptyMessage="No organizations found"
        onRowClick={handleRowClick}
      />

      {/* Pagination */}
      <Pagination
        currentPage={page}
        totalPages={totalPages}
        totalItems={data?.total ?? 0}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
      />

      {/* Create Modal */}
      <CreateOrgModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} />
    </div>
  );
}
